import {
  Box,
  TextField,
  Typography,
  Paper,
  Button,
  alpha
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type Props = {
  customStatement: string;
  defaultStatement: string;
  onUpdateStatement: (statement: string) => void;
  onClearStatement: () => void;
};

export const AcknowledgmentConfigContent = ({
  customStatement,
  defaultStatement,
  onUpdateStatement,
  onClearStatement
}: Props) => {
  const { t } = useTranslation();

  const hasCustomStatement = customStatement.trim().length > 0;

  return (
    <Box
      sx={{
        maxWidth: 800,
        margin: '0 auto',
        p: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('settings:customerAcknowledgment.customStatement.label')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings:customerAcknowledgment.customStatement.helper')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          value={customStatement}
          onChange={e => onUpdateStatement(e.target.value)}
          placeholder={t(
            'settings:customerAcknowledgment.customStatement.placeholder'
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper'
            }
          }}
        />

        {hasCustomStatement && (
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={onClearStatement}
              color="inherit"
              sx={{ color: 'text.secondary' }}
            >
              {t('settings:customerAcknowledgment.customStatement.clear')}
            </Button>
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('settings:customerAcknowledgment.defaultStatement.label')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings:customerAcknowledgment.defaultStatement.helper')}
        </Typography>

        <Paper
          variant="outlined"
          sx={theme => ({
            p: 2,
            backgroundColor: alpha(theme.palette.action.hover, 0.04),
            borderStyle: 'dashed'
          })}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              lineHeight: 1.6
            }}
          >
            {defaultStatement}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
